import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, map } from 'rxjs';

export type EnergyUnit = 'MWh' | 'kWh';

// The app works in MWh and €/MWh internally (and sends those to the backend): values are only converted to show them.
export namespace EnergyUnits {
    export const ALL: EnergyUnit[] = ['MWh', 'kWh'];

    export function convertConsumption(valueInMWh: number, unit: EnergyUnit): number {
        return unit == 'kWh' ? valueInMWh * 1000 : valueInMWh;
    }

    export function convertPrice(valuePerMWh: number, unit: EnergyUnit): number {
        return unit == 'kWh' ? valuePerMWh / 1000 : valuePerMWh;
    }

    // Opportunities store their consumptions in kWh: these only change how a stored kWh value is shown or typed.
    export function kwhToDisplay(valueInKWh: number, unit: EnergyUnit): number {
        return unit == 'kWh' ? valueInKWh : valueInKWh / 1000;
    }

    export function displayToKwh(displayedValue: number, unit: EnergyUnit): number {
        // Rounded to get rid of float noise (1.234 * 1000 = 1234.0000000000002)
        return unit == 'kWh' ? displayedValue : Math.round(displayedValue * 1000 * 1e6) / 1e6;
    }

    // Number format for a value shown in the given unit; MWh is the format the caller would use for MWh.
    // 3 decimals of MWh are whole kWh, and 2 decimals of €/MWh are 5 of €/kWh.
    export function getDigits(type: 'consumption' | 'price', unit: EnergyUnit, mwhDigits: string): string {
        if (unit == 'MWh') {
            return mwhDigits;
        }

        return type == 'consumption' ? '1.0-0' : '1.2-4';
    }

    export function getUnitLabel(type: 'consumption' | 'price', unit: EnergyUnit): string {
        return type == 'price' ? '€/' + unit : unit;
    }
}

// Consumptions (MWh or kWh) and prices (€/MWh or €/kWh) are separate settings, so a user can read, for example, kWh and €/MWh.
@Injectable({
    providedIn: 'root'
})
export class EnergyUnitService {
    private static readonly CONSUMPTION_STORAGE_KEY = 'ConsumptionUnit';
    private static readonly PRICE_STORAGE_KEY = 'PriceUnit';

    readonly consumptionUnit$: BehaviorSubject<EnergyUnit>;
    readonly priceUnit$: BehaviorSubject<EnergyUnit>;

    // Emits when either unit changes, for what has to be rebuilt (charts)
    readonly units$;

    constructor() {
        this.consumptionUnit$ = new BehaviorSubject<EnergyUnit>(this.getUnitFromLocalStorage(EnergyUnitService.CONSUMPTION_STORAGE_KEY) ?? 'MWh');
        this.priceUnit$ = new BehaviorSubject<EnergyUnit>(this.getUnitFromLocalStorage(EnergyUnitService.PRICE_STORAGE_KEY) ?? 'MWh');
        this.units$ = combineLatest([this.consumptionUnit$, this.priceUnit$]).pipe(map(([consumption, price]) => ({ consumption, price })));
    }

    get consumptionUnit(): EnergyUnit {
        return this.consumptionUnit$.value;
    }

    get priceUnit(): EnergyUnit {
        return this.priceUnit$.value;
    }

    setConsumptionUnit(unit: EnergyUnit) {
        if (unit != this.consumptionUnit) {
            this.consumptionUnit$.next(unit);
            this.setUnitOnLocalStorage(EnergyUnitService.CONSUMPTION_STORAGE_KEY, unit);
        }
    }

    setPriceUnit(unit: EnergyUnit) {
        if (unit != this.priceUnit) {
            this.priceUnit$.next(unit);
            this.setUnitOnLocalStorage(EnergyUnitService.PRICE_STORAGE_KEY, unit);
        }
    }

    // The unit of a kind of value: MWh or kWh for consumptions, MWh or kWh (per €) for prices
    getUnit(type: 'consumption' | 'price'): EnergyUnit {
        return type == 'price' ? this.priceUnit : this.consumptionUnit;
    }

    private setUnitOnLocalStorage(key: string, unit: EnergyUnit) {
        try {
            localStorage.setItem(key, unit);
        } catch {
            // The choice just isn't remembered when storage isn't available
        }
    }

    private getUnitFromLocalStorage(key: string): EnergyUnit | null {
        try {
            const stored = localStorage.getItem(key);
            return EnergyUnits.ALL.find((unit) => unit == stored) ?? null;
        } catch {
            return null;
        }
    }
}
